import type { SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { isTwoStageProcedure } from "../../../sourcing-events/buyer/wizard/utils/procedure";

export type ChangeWindow = "open" | "reopenable" | "closed";

/**
 * Mirrors `SourcingEventV2.isWithinChangeWindow` on the BFF — keep the two in step, or the UI will
 * offer a change the server refuses. An FOA Part I procurement runs the private profile too.
 *
 * One deliberate divergence: the server admits a dynamic purchasing system, which races its
 * announced validity rather than a bid deadline. That resolves to null here, so this reports
 * "closed" — the editor has no `dpsValidTo` control, so the change could not be expressed anyway.
 */
export const changeWindowOf = (event: SourcingEventV2Dto, now: number = Date.now()): ChangeWindow => {
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  if (isTwoStageProcedure(publicContext?.procedureType)) return "closed";
  const deadline = event.milestoneDates?.bidSubmissionDeadline;
  if (deadline == null) return "closed";
  if (new Date(deadline).getTime() > now) return "open";
  return event.eventContext.type === "Private" ? "reopenable" : "closed";
};

export interface DeadlineEditing {
  earliestSelectable: Date | undefined;
  canReopen: boolean;
  isClosed: boolean;
  warnsDeadlineIsNear: boolean;
}

const NEAR_DEADLINE_MS = 24 * 60 * 60 * 1000;

export const deadlineEditingOf = (event: SourcingEventV2Dto, now: number = Date.now()): DeadlineEditing => {
  const deadline = event.milestoneDates?.bidSubmissionDeadline;
  const window = changeWindowOf(event, now);
  const isAnnounced = event.eventContext.type !== "Private";
  return {
    earliestSelectable:
      window === "reopenable" ? new Date(now) : deadline != null ? new Date(deadline) : undefined,
    canReopen: window === "reopenable",
    isClosed: window === "closed",
    warnsDeadlineIsNear:
      window === "open" &&
      isAnnounced &&
      deadline != null &&
      new Date(deadline).getTime() - now < NEAR_DEADLINE_MS,
  };
};
